import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { type SurveyAnswer, useAppContext } from "@/contexts";
import { useTrackAccessDisqualified, useTrackEvent } from "@/hooks";
import {
  ACCESS_EXCLUSION_OPTIONS,
  type AccessExclusionOption,
  getAccessQualification,
  hasAccessExclusion,
  isAccessExclusionOptions,
} from "@/utils/cmsAccess";
import { isCmsAccessEnabled } from "@/utils/partners";

import {
  QuestionComponentProps,
  QuestionDefinition,
  QuestionOption,
} from "../../Question";
import { CheckboxQuestion } from "../core";

const QUESTION_ID = "cmsAccessDisqualifyingConditions";

declare module "@/contexts" {
  interface SurveyAnswers {
    cmsAccessDisqualifyingConditions?: AccessExclusionOption[];
  }
}

const AccessExclusionsQuestion: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
  surveyAnswers,
}) => {
  const { t } = useTranslation();
  const { setSurveyAnswers } = useAppContext();
  const { trackAnonEvent } = useTrackEvent();
  const trackAccessDisqualified = useTrackAccessDisqualified();

  useEffect(() => {
    // The checkboxes start empty, so clear the prior answer in case of back
    // navigation to keep the stored answer in sync with the screen.
    setSurveyAnswers({ ...surveyAnswers, [QUESTION_ID]: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `CheckboxQuestion` passes the generic `SurveyAnswer` type, so the answer is
  // narrowed to the ACCESS options before it is used.
  const handleClickNext = async (answer: SurveyAnswer) => {
    trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName: "Continue",
      pageId: QUESTION_ID,
    });
    if (!isAccessExclusionOptions(answer)) {
      return;
    }
    if (hasAccessExclusion(answer)) {
      trackAccessDisqualified();
    }
    await onClickNext(answer);
  };

  const options: QuestionOption[] = ACCESS_EXCLUSION_OPTIONS.map((id) => ({
    id,
    text: t(`cmsAccessDisqualifyingConditions.options.${id}`),
  }));

  return (
    <CheckboxQuestion
      ctaText={t("cmsAccessDisqualifyingConditions.ctaText")}
      isCtaSticky
      onClickBack={onClickBack}
      onClickNext={handleClickNext}
      options={options}
      questionHelperText={t("cmsAccessDisqualifyingConditions.helperText")}
      questionId={QUESTION_ID}
      questionText={t("cmsAccessDisqualifyingConditions.questionText")}
      // Previous answers are intentionally not restored: when the member comes
      // back to this screen, they answer again from scratch.
      selectedItems={[]}
    />
  );
};

export const Q_ACCESS_EXCLUSIONS: QuestionDefinition = {
  id: QUESTION_ID,
  // Members who do not qualify for ACCESS go straight to the "not eligible"
  // screen, so only qualified members get this list.
  shouldShowQuestion: ({ partnerInfo, surveyAnswers }) =>
    isCmsAccessEnabled(partnerInfo) &&
    getAccessQualification(surveyAnswers.cmsAccessQualifyingConditions) ===
      "QUALIFIED",
  component: AccessExclusionsQuestion,
};
